import React, { useEffect, useState, useCallback } from 'react';
import { X, Loader2, Save, ChevronRight, ChevronDown } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import { FLEET_PERMISSION, PERMISSION_GROUPS } from '../../utils/fleetPermissions';
import './SupervisorsPage.css';

/** İşaretli kutuları tek sayıya toplar. Backend maskeyi sayı bekler. */
export function permissionsFromChecks(checks) {
    return Object.entries(checks)
        .filter(([, checked]) => checked)
        .reduce((mask, [key]) => mask | (FLEET_PERMISSION[key] || 0), 0);
}

/** En az bir bölge seçilmiş mi? Backend'in "FleetSupervisorAreaRequired" kuralını istemci tarafında kilitler. */
export function hasAnyArea(form) {
    return form.selectedDistricts.length > 0 || form.selectedRegions.length > 0 || form.selectedSubRegions.length > 0;
}

/** Kapsam ağacı seçimini TEK SEVİYELİ satırlara çevirir (backend karışık satırı reddeder). */
export function buildSupervisorPayload(form) {
    const areas = [
        ...form.selectedDistricts.map(id => ({ districtId: id, regionId: null, subRegionId: null })),
        ...form.selectedRegions.map(id => ({ districtId: null, regionId: id, subRegionId: null })),
        ...form.selectedSubRegions.map(id => ({ districtId: null, regionId: null, subRegionId: id })),
    ];

    return {
        displayName: form.displayName.trim(),
        phoneNumber: String(form.phoneNumber || '').replace(/\D/g, ''),
        email: form.email?.trim() || null,
        permissions: permissionsFromChecks(form.checks),
        areas,
    };
}

const EMPTY_FORM = { displayName: '', phoneNumber: '', email: '' };

/** permissions maskesinden { DeliveryView: true, ... } gibi bir checks nesnesi üretir. */
function checksFromPermissions(mask) {
    const checks = {};
    Object.keys(FLEET_PERMISSION).forEach((key) => {
        checks[key] = (Number(mask || 0) & FLEET_PERMISSION[key]) === FLEET_PERMISSION[key];
    });
    return checks;
}

/** Bir bölgenin/mahallenin üst düğümü zaten seçiliyse kendi kutusu devre dışı kalır — tek satır kuralı. */
function districtSelectedCount(district, selectedRegions, selectedSubRegions) {
    const regionIds = district.regions.map((r) => r.regionId);
    const subIds = district.regions.flatMap((r) => r.subRegions.map((s) => s.subRegionId));
    return regionIds.filter((id) => selectedRegions.includes(id)).length
        + subIds.filter((id) => selectedSubRegions.includes(id)).length;
}

const SupervisorFormModal = ({ mode, supervisor, onClose, onSaved }) => {
    const isEdit = mode === 'edit';

    const [form, setForm] = useState(() => (isEdit
        ? { displayName: supervisor.displayName || '', phoneNumber: supervisor.phoneNumber || '', email: supervisor.email || '' }
        : EMPTY_FORM));
    const [checks, setChecks] = useState(() => checksFromPermissions(isEdit ? supervisor.permissions : 0));

    const initialAreas = isEdit ? (supervisor.areas || []) : [];
    const [selectedDistricts, setSelectedDistricts] = useState(() => initialAreas.filter(a => a.districtId).map(a => a.districtId));
    const [selectedRegions, setSelectedRegions] = useState(() => initialAreas.filter(a => a.regionId).map(a => a.regionId));
    const [selectedSubRegions, setSelectedSubRegions] = useState(() => initialAreas.filter(a => a.subRegionId).map(a => a.subRegionId));

    const [scopeOptions, setScopeOptions] = useState([]);
    const [loadingScope, setLoadingScope] = useState(true);
    const [expandedDistricts, setExpandedDistricts] = useState([]);
    const [expandedRegions, setExpandedRegions] = useState([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoadingScope(true);
        apiService.get(API_ENDPOINTS.FLEET_PANEL.SCOPE_OPTIONS)
            .then((res) => {
                if (cancelled) return;
                if (res.success) setScopeOptions(res.data || []);
                else fleetToast.error(res.error || 'Kapsam listesi alınamadı');
            })
            .finally(() => { if (!cancelled) setLoadingScope(false); });
        return () => { cancelled = true; };
    }, []);

    const toggleExpandDistrict = (districtId) => {
        setExpandedDistricts(prev => prev.includes(districtId) ? prev.filter(id => id !== districtId) : [...prev, districtId]);
    };
    const toggleExpandRegion = (regionId) => {
        setExpandedRegions(prev => prev.includes(regionId) ? prev.filter(id => id !== regionId) : [...prev, regionId]);
    };

    const toggleDistrict = (district) => {
        const regionIds = district.regions.map(r => r.regionId);
        const subIds = district.regions.flatMap(r => r.subRegions.map(s => s.subRegionId));
        setSelectedDistricts((prev) => {
            if (prev.includes(district.districtId)) return prev.filter(id => id !== district.districtId);
            return [...prev, district.districtId];
        });
        if (!selectedDistricts.includes(district.districtId)) {
            // Yeni seçiliyor: üst düğüm alt düğümleri kapsar, çakışan satırları temizle.
            setSelectedRegions(prev => prev.filter(id => !regionIds.includes(id)));
            setSelectedSubRegions(prev => prev.filter(id => !subIds.includes(id)));
        }
    };

    const toggleRegion = (district, region) => {
        if (selectedDistricts.includes(district.districtId)) return; // üst düğüm seçiliyken kilitli
        const subIds = region.subRegions.map(s => s.subRegionId);
        setSelectedRegions((prev) => {
            if (prev.includes(region.regionId)) return prev.filter(id => id !== region.regionId);
            return [...prev, region.regionId];
        });
        if (!selectedRegions.includes(region.regionId)) {
            setSelectedSubRegions(prev => prev.filter(id => !subIds.includes(id)));
        }
    };

    const toggleSubRegion = (district, region, subRegionId) => {
        if (selectedDistricts.includes(district.districtId) || selectedRegions.includes(region.regionId)) return;
        setSelectedSubRegions((prev) => (
            prev.includes(subRegionId) ? prev.filter(id => id !== subRegionId) : [...prev, subRegionId]
        ));
    };

    const toggleCheck = (key) => setChecks(prev => ({ ...prev, [key]: !prev[key] }));

    const totalSelectedAreas = selectedDistricts.length + selectedRegions.length + selectedSubRegions.length;

    const handleSave = async () => {
        if (saving) return;
        if (!form.displayName.trim()) { fleetToast.error('Ad Soyad zorunludur'); return; }
        const digits = String(form.phoneNumber || '').replace(/\D/g, '');
        if (digits.length < 10) { fleetToast.error('Geçerli bir telefon numarası girin'); return; }
        if (!hasAnyArea({ selectedDistricts, selectedRegions, selectedSubRegions })) {
            fleetToast.error('En az bir bölge seçmelisiniz');
            return;
        }

        setSaving(true);
        try {
            const payload = buildSupervisorPayload({ ...form, checks, selectedDistricts, selectedRegions, selectedSubRegions });

            if (isEdit) {
                const updateRes = await apiService.put(API_ENDPOINTS.FLEET_PANEL.SUPERVISOR_DETAIL(supervisor.id), {
                    displayName: payload.displayName,
                    email: payload.email,
                    permissions: payload.permissions,
                });
                if (!updateRes.success) { fleetToast.error(updateRes.error || 'Güncelleme başarısız'); return; }

                const areasRes = await apiService.put(API_ENDPOINTS.FLEET_PANEL.SUPERVISOR_AREAS(supervisor.id), { areas: payload.areas });
                if (!areasRes.success) {
                    // Bilgiler zaten kaydedildi — burada onSaved() ÇAĞRILMAZ, modal açık kalır ki kullanıcı
                    // bölge güncellemesini yeniden deneyebilsin. Genel bir "işlem başarısız" mesajı kullanıcıya
                    // ad/e-posta/yetki değişikliğinin de kaybolduğunu düşündürür — dürüst olan budur.
                    fleetToast.error('Bilgiler kaydedildi ancak bölge güncellemesi başarısız oldu. Bölgeleri tekrar kaydedin.');
                    return;
                }

                fleetToast.success('Sorumlu güncellendi');
            } else {
                const res = await apiService.post(API_ENDPOINTS.FLEET_PANEL.SUPERVISORS, payload);
                if (!res.success) { fleetToast.error(res.error || 'Sorumlu eklenemedi'); return; }
                fleetToast.success('Sorumlu eklendi');
            }
            onSaved();
        } catch {
            fleetToast.error('Beklenmeyen bir hata oluştu');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal modal-wide sv-form-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{isEdit ? 'Sorumlu Düzenle' : 'Yeni Bölge Sorumlusu'}</h2>
                    <button className="modal-close" onClick={onClose}><X size={20} /></button>
                </div>

                <div className="modal-body">
                    <div className="form-grid">
                        <div className="form-group">
                            <label>Ad Soyad <span className="required">*</span></label>
                            <input
                                type="text"
                                value={form.displayName}
                                onChange={(e) => setForm(f => ({ ...f, displayName: e.target.value }))}
                                placeholder="Ad Soyad"
                            />
                        </div>
                        <div className="form-group">
                            <label>Telefon <span className="required">*</span></label>
                            <input
                                type="tel"
                                value={form.phoneNumber}
                                disabled={isEdit}
                                onChange={(e) => setForm(f => ({ ...f, phoneNumber: e.target.value }))}
                                placeholder="0555 111 22 33"
                            />
                            {isEdit && <p className="sv-field-hint">Telefon numarası düzenlenemez.</p>}
                        </div>
                        <div className="form-group full-width">
                            <label>E-posta</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                                placeholder="ornek@hizliyo.com"
                            />
                        </div>
                    </div>

                    <div className="sv-section">
                        <div className="sv-section-header">
                            <span>Bölgeler</span>
                            {totalSelectedAreas > 0 && <span className="count-badge">{totalSelectedAreas} seçili</span>}
                        </div>
                        {!loadingScope && totalSelectedAreas === 0 && (
                            <p className="sv-field-hint sv-area-required-hint">En az bir bölge seçin</p>
                        )}

                        {loadingScope ? (
                            <div className="page-loading" style={{ padding: '24px 0' }}>
                                <Loader2 className="spinning" size={24} />
                            </div>
                        ) : scopeOptions.length === 0 ? (
                            <p className="sv-empty-hint">Tanımlı kapsama alanı bulunamadı.</p>
                        ) : (
                            <div className="sv-scope-tree">
                                {scopeOptions.map((district) => {
                                    const districtSelected = selectedDistricts.includes(district.districtId);
                                    const districtExpanded = expandedDistricts.includes(district.districtId);
                                    const childCount = districtSelectedCount(district, selectedRegions, selectedSubRegions);
                                    return (
                                        <div key={district.districtId} className="sv-tree-node">
                                            <div className="sv-tree-row">
                                                <button
                                                    type="button"
                                                    className="sv-tree-toggle"
                                                    onClick={() => toggleExpandDistrict(district.districtId)}
                                                    disabled={district.regions.length === 0}
                                                >
                                                    {district.regions.length > 0 && (districtExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                                                </button>
                                                <label className="sv-tree-label">
                                                    <input type="checkbox" checked={districtSelected} onChange={() => toggleDistrict(district)} />
                                                    <span>{district.districtName}</span>
                                                    <span className="sv-tree-kind">ilçe</span>
                                                </label>
                                                {!districtSelected && childCount > 0 && <span className="sv-tree-count">{childCount} seçili</span>}
                                            </div>

                                            {districtExpanded && (
                                                <div className="sv-tree-children">
                                                    {district.regions.map((region) => {
                                                        const regionSelected = districtSelected || selectedRegions.includes(region.regionId);
                                                        const regionExpanded = expandedRegions.includes(region.regionId);
                                                        const regionSubSelected = region.subRegions.filter(s => selectedSubRegions.includes(s.subRegionId)).length;
                                                        return (
                                                            <div key={region.regionId} className="sv-tree-node">
                                                                <div className="sv-tree-row">
                                                                    <button
                                                                        type="button"
                                                                        className="sv-tree-toggle"
                                                                        onClick={() => toggleExpandRegion(region.regionId)}
                                                                        disabled={region.subRegions.length === 0}
                                                                    >
                                                                        {region.subRegions.length > 0 && (regionExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
                                                                    </button>
                                                                    <label className="sv-tree-label">
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={regionSelected}
                                                                            disabled={districtSelected}
                                                                            onChange={() => toggleRegion(district, region)}
                                                                        />
                                                                        <span>{region.regionName}</span>
                                                                        <span className="sv-tree-kind">bölge</span>
                                                                    </label>
                                                                    {!regionSelected && regionSubSelected > 0 && <span className="sv-tree-count">{regionSubSelected} seçili</span>}
                                                                </div>

                                                                {regionExpanded && (
                                                                    <div className="sv-tree-children">
                                                                        {region.subRegions.map((sub) => {
                                                                            const subSelected = regionSelected || selectedSubRegions.includes(sub.subRegionId);
                                                                            return (
                                                                                <div key={sub.subRegionId} className="sv-tree-row">
                                                                                    <span className="sv-tree-toggle" />
                                                                                    <label className="sv-tree-label">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={subSelected}
                                                                                            disabled={regionSelected}
                                                                                            onChange={() => toggleSubRegion(district, region, sub.subRegionId)}
                                                                                        />
                                                                                        <span>{sub.subRegionName}</span>
                                                                                    </label>
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="sv-section">
                        <div className="sv-section-header"><span>Yetkiler</span></div>
                        <div className="sv-perm-groups">
                            {PERMISSION_GROUPS.map((group) => (
                                <div key={group.label} className="sv-perm-group">
                                    <div className="sv-perm-group-title">{group.label}</div>
                                    {group.keys.map(([key, label]) => (
                                        <label key={key} className="sv-perm-item">
                                            <input type="checkbox" checked={!!checks[key]} onChange={() => toggleCheck(key)} />
                                            <span>{label}</span>
                                        </label>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={onClose} disabled={saving}>İptal</button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving || totalSelectedAreas === 0}>
                        {saving ? <Loader2 size={16} className="spinning" /> : <Save size={16} />}
                        {saving ? 'Kaydediliyor...' : 'Kaydet'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SupervisorFormModal;
